import { UserJob, type UserJobType } from "@redotech/account-common/job-level";
import Announcement02Icon from "@redotech/redo-icons/announcement-02.svg";
import Award01Icon from "@redotech/redo-icons/award-01.svg";
import Box01Icon from "@redotech/redo-icons/box-01.svg";
import CodeSnippet01Icon from "@redotech/redo-icons/code-snippet-01.svg";
import Coins01Icon from "@redotech/redo-icons/coins-01.svg";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import Globe02Icon from "@redotech/redo-icons/globe-02.svg";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import ShoppingCart01Icon from "@redotech/redo-icons/shopping-cart-01.svg";
import Star01Icon from "@redotech/redo-icons/star-01.svg";
import Truck01Icon from "@redotech/redo-icons/truck-01.svg";
import WarehouseIcon from "@redotech/redo-icons/warehouse.svg";
import type { ElementType } from "react";

export interface UserJobGroup {
  key: string;
  label: string;
  Icon: ElementType<{ className?: string }>;
  members: UserJobType[];
}

// Collapses the granular UserJob values into the card set merchants pick from;
// `members` is what actually persists to jobProfile.job.
export const USER_JOB_GROUPS: UserJobGroup[] = [
  {
    key: "founder",
    label: "Founder / Owner",
    Icon: Star01Icon,
    members: [UserJob.FOUNDER_OWNER],
  },
  {
    key: "executive",
    label: "Executive",
    Icon: Award01Icon,
    members: [UserJob.C_SUITE],
  },
  {
    key: "marketing",
    label: "Marketing",
    Icon: Announcement02Icon,
    members: [
      UserJob.MARKETING_EMAIL_SMS_RETENTION,
      UserJob.MARKETING_PAID_GROWTH_ACQUISITION,
      UserJob.MARKETING_BRAND_CREATIVE,
    ],
  },
  {
    key: "ecommerce",
    label: "Ecommerce",
    Icon: ShoppingCart01Icon,
    members: [UserJob.ECOMMERCE_DIGITAL],
  },
  {
    key: "inventory",
    label: "Inventory",
    Icon: Box01Icon,
    members: [UserJob.INVENTORY_PROCUREMENT_FORECASTING],
  },
  {
    key: "warehouse",
    label: "Warehouse",
    Icon: WarehouseIcon,
    members: [UserJob.WAREHOUSE_MANAGER, UserJob.WAREHOUSE_OPERATIONS],
  },
  {
    key: "shipping",
    label: "Shipping",
    Icon: Truck01Icon,
    members: [UserJob.SHIPPING_CARRIER_OPERATIONS],
  },
  {
    key: "returns",
    label: "Returns",
    Icon: FlipBackwardIcon,
    members: [UserJob.RETURNS_REVERSE_LOGISTICS],
  },
  {
    key: "supply-chain",
    label: "Supply Chain",
    Icon: Globe02Icon,
    members: [UserJob.THIRD_PL_SUPPLY_CHAIN],
  },
  {
    key: "cx",
    label: "Support",
    Icon: MessageChatCircleIcon,
    members: [UserJob.CUSTOMER_EXPERIENCE_SUPPORT],
  },
  {
    key: "finance",
    label: "Finance",
    Icon: Coins01Icon,
    members: [UserJob.FINANCE_ANALYTICS],
  },
  {
    key: "technology",
    label: "Technology",
    Icon: CodeSnippet01Icon,
    members: [UserJob.TECHNOLOGY_IT_DEVELOPMENT],
  },
];

export function resolveJobsFromGroupKeys(
  groupKeys: ReadonlySet<string>,
  existingJobs: readonly UserJobType[] | null | undefined,
): UserJobType[] {
  const existing = new Set(existingJobs ?? []);
  return USER_JOB_GROUPS.filter((group) => groupKeys.has(group.key)).flatMap(
    (group) => {
      const existingGroupJobs = group.members.filter((job) =>
        existing.has(job),
      );
      return existingGroupJobs.length > 0 ? existingGroupJobs : group.members;
    },
  );
}

export function resolveGroupKeysFromJobs(
  jobs: readonly UserJobType[] | null | undefined,
): Set<string> {
  if (!jobs?.length) {
    return new Set();
  }
  const selected = new Set(jobs);
  return new Set(
    USER_JOB_GROUPS.filter((group) =>
      group.members.some((member) => selected.has(member)),
    ).map((group) => group.key),
  );
}
